/**
 * Single source of truth for the Action Centre (active) ⇄ History (archive)
 * split. A record belongs to History when it is closed / completed / past.
 *
 * Both the per-tab bodies AND useWorkCounts import these predicates, so the
 * rail's pending/archived counts always match exactly what each tab renders.
 * If a module's terminal status set turns out to differ, fix it HERE only.
 */

// SOP + statutory process instances (status enum: PENDING/IN_PROGRESS/COMPLETED/CANCELLED)
export const isClosedInstance = (status?: string | null): boolean => {
  if (!status) return false;
  const s = status.toUpperCase();
  return s === 'COMPLETED' || s === 'CANCELLED';
};

// CAT / RAT / PWT programs (free-text workflow status). Terminal == approved/closed/done.
export const isClosedProgram = (status?: string | null): boolean => {
  if (!status) return false;
  const s = status.toUpperCase();
  return ['COMPLETED', 'COMPLETE', 'CLOSED', 'APPROVED', 'DONE', 'ARCHIVED', 'REJECTED'].includes(s);
};

// PRM projects (status enum: DRAFT/ACTIVE/ON_HOLD/COMPLETED/CANCELLED).
// A closed project leaves everyone's "My Projects" and lives in History.
export const isClosedProject = (status?: string | null): boolean => {
  if (!status) return false;
  const s = status.toUpperCase();
  return s === 'COMPLETED' || s === 'CANCELLED';
};

// Litigation instances (free-text status). Terminal == closed/resolved/disposed/settled.
export const isClosedLitigation = (status?: string | null): boolean =>
  !!status && /clos|resolv|dispos|settl/i.test(status);

// Renewals / reviews cadence obligations — bucket 'completed' is the archive set.
export const isCompletedRenewalReview = (bucket?: string | null): boolean =>
  bucket === 'completed';

// Meetings — closed when concluded/cancelled OR scheduled in the past.
export const isClosedMeeting = (m: { meeting_status?: string | null; scheduled_date?: string | null }): boolean => {
  const s = (m.meeting_status || '').toUpperCase();
  if (['COMPLETED', 'CLOSED', 'CANCELLED', 'CONCLUDED', 'DONE'].includes(s)) return true;
  if (m.scheduled_date) {
    const d = new Date(m.scheduled_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (!Number.isNaN(d.getTime()) && d < today) return true;
  }
  return false;
};

/**
 * The rows a tab body should render for the given scope.
 * - 'active'  → the list UNCHANGED (Action Centre behaviour is preserved exactly).
 * - 'archive' → only the closed/completed/past rows.
 * Use this in every body so History filtering never alters the live Action Centre.
 */
export function visibleForScope<T>(
  rows: T[],
  isClosed: (row: T) => boolean,
  scope: 'active' | 'archive',
): T[] {
  return scope === 'archive' ? rows.filter(isClosed) : rows;
}
